<script setup lang="ts">
import { computed, ref } from 'vue'
import { FORM_INPUT_TYPE } from '@/components/forms/form.enum'

const props = defineProps({
  id: {
    type: String,
    default: null
  },
  label: {
    type: String,
    default: ''
  },
  showLable: {
    type: Boolean,
    default: true
  },
  name: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  autocomplete: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  },
  modelValue: {
    required: true
  },
  inputType: {
    type: String,
    required: false
  },
  disabled: {
    type: Boolean,
    required: false
  },
  maxInt: {
    type: Number,
    required: false
  },
  minInt: {
    type: Number,
    required: false
  },
  isContactus: {
    type: Boolean,
    required: false,
    default: false
  },
  isLightGray: {
    type: Boolean,
    required: false,
    default: true
  },
  hasBorder: {
    type: Boolean,
    required: false,
    default: true
  },
  isSmallLabel: {
    type: Boolean,
    required: false,
    default: false
  },
  isBoldLabel: {
    type: Boolean,
    required: false,
    default: false
  },
  isLabelBold: {
    type: Boolean,
    required: false,
    default: false
  },
  isSettings: {
    type: Boolean,
    required: false,
    default: false
  },
  isEditable: {
    type: Boolean,
    required: false,
    default: false
  },
  isPassword: {
    type: Boolean,
    required: false,
    default: false
  },
  isNewPassword: {
    type: Boolean,
    required: false,
    default: false
  },
  isEmail: {
    type: Boolean,
    required: false,
    default: false
  },
  success: {
    type: Boolean,
    required: false,
    default: false
  },
  isLightMode: {
    type: Boolean,
    required: false,
    default: false
  },
  isPasswordIcon: {
    type: String,
    required: false
  },
  isPasswordStyling: {
    type: String,
    required: false
  },
  labelClass: {
    type: String,
    requried: false,
    default: ''
  },
  isEnterKeySupport: {
    type: Boolean,
    required: false,
    default: false
  },
  isCustomClass: {
    type: String,
    required: false,
    default: ''
  }
})
const input = ref<HTMLElement | null>(null)

const inputClasses = computed(() => {
  switch (true) {
    case props.isContactus:
      return 'section-primary border-b px-1 pb-3'
    case props.isLightGray:
      return 'rounded-md bg-neutral-100 px-4 dark:bg-[#17191F]'
    case props.isLabelBold:
      return 'font-bold'
    case props.isSettings:
      return 'rounded-lg py-5 p-xsmall mb-4 font-semibold'
    case props.isLightMode:
      return 'bg-gray-100 text-text-primary autofill:text-fill-text-primary-DEFAULT autofill:shadow-fill-background-primary-DEFAULT rounded-md px-4'
    default:
      return `rounded-md px-4 section-primary`
  }
})

const emit = defineEmits(['update:modelValue', 'click', 'check', 'enter'])
/**
 * Emit for Toggle to show and hide password
 */
const handleClick = () => {
  if (props.isEnterKeySupport) {
    emit('click')
  }
}

/**
 * @param event on change
 * Emit for password validation
 */
const handleInput = (event: Event) => {
  emit('check', (event.target as HTMLInputElement).value)
}

/**
 * enable focus on input for UserName
 */
const focusInput = () => {
  input.value?.focus()
}
/**
 * Emits an 'enter' event.
 */
const onEnter = () => {
  emit('enter')
}
</script>

<template>
  <div :class="[{ 'my-2': !showLable }, isSettings ? 'mx-2 lg:mx-0' : '']">
    <div v-if="showLable" class="flex items-center justify-between">
      <label
        :for="props.name || `${props.label + props.placeholder}`"
        class="my-2 block text-base"
        :class="[
          props.labelClass,
          props.isSmallLabel ? 'text-sm' : '',
          props.isSettings ? 'text-xs font-semibold md:text-sm lg:text-base' : '',
          props.isBoldLabel ? 'font-medium' : ''
        ]"
        v-html="props.label"
      >
      </label>
      <slot />
    </div>
    <div class="relative">
      <input
        ref="input"
        v-bind="$attrs"
        :id="props.id || props.name || `${props.label + props.placeholder}`"
        class="p-small mb-1 block w-full"
        :class="[
          props.error ? 'border-red-500' : 'border-text-tertiary',
          props.isEmail ? 'text-btn-primary-disabled' : '',
          props.isSettings ? 'p-small md:p-normal py-1' : '',
          inputClasses,
          props.isCustomClass,
          props.isEnterKeySupport ? 'py-2' : 'py-3',

          { border: hasBorder }
        ]"
        :placeholder="props.placeholder"
        :autocomplete="props.autocomplete || undefined"
        :value="modelValue"
        :max="maxInt"
        :min="minInt"
        :disabled="props.disabled"
        :type="props.inputType ? props.inputType : FORM_INPUT_TYPE.STRING"
        @change="(event: Event) => {
          props.inputType == FORM_INPUT_TYPE.NUMBER ?
            emit('update:modelValue', Number.parseInt((event.target as HTMLInputElement).value)) :
            emit('update:modelValue', (event.target as HTMLInputElement).value)
        }"
        @input="handleInput"
        @keydown.enter="onEnter"
      />
      <div
        :class="[
          'absolute inset-y-0 right-0 flex cursor-pointer items-center pr-2',
          { hidden: !props.error || props.isEmail }
        ]"
        @click="emit('update:modelValue', '')"
      >
        <FontAwesomeIcon
          icon="fa-circle-xmark"
          class=""
          :class="[
            props.isSettings
              ? 'text-brand absolute right-2 top-3 h-5 xl:right-[-25px]'
              : 'error h-4 w-4'
          ]"
        />
      </div>
      <div
        v-if="props.isEditable && !props.error && !props.success"
        class="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-2"
      >
        <FontAwesomeIcon icon="fa-pen" class="h-4 w-4 flex-1" @click="focusInput" />
      </div>
      <button
        v-if="props.isPassword && !props.error && !props.success"
        type="button"
        class="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-2"
      >
        <font-awesome-icon
          v-if="props.isPasswordIcon"
          :class="props.isPasswordStyling"
          @click="handleClick"
          :icon="props.isPasswordIcon"
        />
      </button>
    </div>
    <p
      :class="[
        'p-xsmall error ',
        { hidden: !props.error },
        props.isEmail ? 'text-right' : 'text-left'
      ]"
    >
      {{ props.error.charAt(0).toUpperCase() + props.error.slice(1) }}
    </p>
    <p
      :class="['p-xsmall ', 'error']"
      v-if="props.inputType == FORM_INPUT_TYPE.NUMBER && (modelValue as number) >= (maxInt as number)"
    >
      Number must be less than {{ props.maxInt }}
    </p>
  </div>
</template>
